<script setup>
import { computed, ref, watch } from 'vue';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import IDateRangePicker from '@components/IDateRangePicker/IDateRangePicker.vue';
import useSiteStore from '@vueApp/store/modules/site';
import MultiSelect from '@components/MultiSelect.vue';

const siteStore = useSiteStore();

const filterModel = ref({
  units: [],
  dateRange: [],
  selectTypes: [],
});

const unitsOptions = ref(window.FILTER_UNITS_OPTIONS || []);
const nomsOptions = ref(window.FILTER_NOMS_OPTIONS || []);

const showClearButton = computed(
  () => filterModel.value.units.length
    || filterModel.value.dateRange.length
    || filterModel.value.selectTypes.length,
);

const isMobile = computed(() => window.innerWidth <= 991);
const showFilters = isMobile.value ? ref(false) : ref(true);

function getDateRangeParam() {
  if (!filterModel.value.dateRange[0]) return [];
  return `${filterModel.value.dateRange[0]} — ${filterModel.value.dateRange[1]}`;
}

function applyFilter() {
  siteStore.setSelectTypes(filterModel.value.selectTypes);
  siteStore.resetAllPages();

  const searchParams = {
    dateRange: getDateRangeParam(),
    objectTags: filterModel.value.units,
  };

  siteStore.setCurrentFilters(searchParams);
  siteStore.searchEvents({ searchParams });
  siteStore.searchPerformances({ searchParams });
  siteStore.searchSubscriptions({ searchParams });
  siteStore.searchServices({ searchParams });
}

function clearFilters() {
  filterModel.value.units = [];
  filterModel.value.dateRange = [];
  filterModel.value.selectTypes = [];
}

watch(filterModel.value, () => applyFilter());
</script>

<template>
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no">
  <div class="events-filter">
    <div class="container">
      <div class="filter-wrapper">
        <i-date-range-picker v-model="filterModel.dateRange" class="w-100 w-lg-25" data-test-id="filter_date" />
        <MultiSelect v-model="filterModel.selectTypes" class="m-0 w-100 w-lg-25" :options="nomsOptions"
          :multiple-label="`${translator.t('main', 'Categories')}:`" :placeholder="translator.t('main', 'Categories')"
          :no-options-text="translator.t('main', 'The list is empty')" :append-to-body="true"
          data-test-id="filter_tournaments" />
        <MultiSelect v-model="filterModel.units" class="m-0 w-100 w-lg-25" :options="unitsOptions"
          :multiple-label="`${translator.t('main', 'Areas')}:`" :placeholder="translator.t('main', 'Areas')"
          :no-options-text="translator.t('main', 'The list is empty')" :append-to-body="true"
          data-test-id="filter_areas" />
        <div v-if="showClearButton" class="d-flex align-items-center text-white flex-grow-1 justify-content-end"
          role="button" @click="clearFilters">
          <svg height="15" width="15" color="white" class="mr-1">
            <use xlink:href="#close" />
          </svg>
          {{ translator.t("main", "Reset") }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@vueform/multiselect/themes/default.css";

.events-filter {
  position: relative;
  z-index: 2;
}

.filter-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 28px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: $screen-md) {
    gap: 12px;
  }
}

.i-filter {
  &__icon {
    transition: transform 0.3s;

    &.active {
      transform: rotate(-180deg);
    }
  }
}
</style>
